import React from "react";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import SoftBox from "../../components/SoftBox";
import SoftButton from "../../components/SoftButton";
import SoftTypography from "../../components/SoftTypography";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";

import { BID_QUALITY_QUESTIONS, BID_QUALITY_POINTS_PER_YES, readStoredAnswer } from "../../constants/bidQuality";

import { T, FONT, MICRO_LABEL_SX, FIGURE_SX, TONE, scoreBand } from "./bidQualityTokens";
import { AnswerPill, ScoreChip } from "./BidQualityChrome";

const pointsLabel = (answer) => {
  if (answer === true) return `+${BID_QUALITY_POINTS_PER_YES}`;
  return answer === false ? "0" : "—";
};

const SectionLabel = ({ children }) => (
  <SoftTypography
    sx={{
      ...MICRO_LABEL_SX,
      fontSize: "11px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: "8px",
    }}
  >
    {children}
  </SoftTypography>
);

/**
 * Everything about one submission in a single place — the table clamps the job
 * name and comments to two lines, so this is where the full text (and the
 * Yes/No breakdown behind the score) lives. Opened by clicking the Job or
 * Comments cell, or the card on mobile.
 */
const SubmissionDetailDialog = ({ row, repName, avatar, onClose }) => {
  const meta = row?.metadata || {};
  const band = scoreBand(meta.score);

  return (
    <Dialog
      open={Boolean(row)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px", margin: "16px", width: "calc(100% - 32px)" } }}
    >
      {row && (
        <SoftBox sx={{ fontFamily: FONT }}>
          {/* head: who, when, and the score they earned */}
          <SoftBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              padding: "20px 24px",
              backgroundColor: T.subtle,
              borderBottom: `1px solid ${T.hairline}`,
            }}
          >
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
              <SoftProfile src={avatar} name={repName} width="44px" height="44px" fullCover />
              <SoftBox sx={{ minWidth: 0 }}>
                <SoftTypography sx={{ fontFamily: FONT, fontSize: "16px", fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                  {repName}
                </SoftTypography>
                <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "12.5px" }}>
                  {moment(row.createdAt).format("MMM D, YYYY · h:mm A")}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
              <SoftBox sx={{ textAlign: "right" }}>
                <ScoreChip score={meta.score} />
                <SoftTypography
                  sx={{ fontFamily: FONT, fontSize: "11px", fontWeight: 600, color: band.ink, marginTop: "3px" }}
                >
                  {band.label}
                </SoftTypography>
              </SoftBox>
              <SoftBox
                component="button"
                type="button"
                onClick={onClose}
                aria-label="Close"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  border: "none",
                  borderRadius: "8px",
                  background: "transparent",
                  color: T.muted,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: T.hairline, color: T.ink },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: "1.2rem" }} />
              </SoftBox>
            </SoftBox>
          </SoftBox>

          <SoftBox sx={{ padding: "20px 24px 8px", display: "flex", flexDirection: "column", gap: "22px" }}>
            <SoftBox>
              <SectionLabel>Job</SectionLabel>
              <SoftTypography
                sx={{
                  fontFamily: FONT,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: meta.jobName ? T.ink : T.faint,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {meta.jobName || "No job name"}
              </SoftTypography>
            </SoftBox>

            <SoftBox>
              <SectionLabel>Breakdown</SectionLabel>
              <SoftBox sx={{ border: `1px solid ${T.line}`, borderRadius: "12px", overflow: "hidden" }}>
                {BID_QUALITY_QUESTIONS.map((q, i) => {
                  const answer = readStoredAnswer(meta[q.key]);
                  return (
                    <SoftBox
                      key={q.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 14px",
                        borderTop: i === 0 ? "none" : `1px solid ${T.hairline}`,
                      }}
                    >
                      <AnswerPill value={answer} iconOnly />
                      <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                        <SoftTypography sx={{ fontFamily: FONT, fontSize: "13.5px", fontWeight: 600, color: T.ink, lineHeight: 1.3 }}>
                          {q.title}
                        </SoftTypography>
                        <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "12px" }}>{q.hint}</SoftTypography>
                      </SoftBox>
                      <SoftTypography
                        sx={{
                          ...FIGURE_SX,
                          fontSize: "12.5px",
                          fontWeight: 700,
                          color: answer === true ? TONE.good.ink : T.faint,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {pointsLabel(answer)}
                      </SoftTypography>
                    </SoftBox>
                  );
                })}
              </SoftBox>
            </SoftBox>

            <SoftBox>
              <SectionLabel>Comments</SectionLabel>
              {meta.comments ? (
                <SoftTypography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "14px",
                    fontWeight: 400,
                    color: T.ink2,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    backgroundColor: T.field,
                    borderRadius: "10px",
                    padding: "12px 14px",
                  }}
                >
                  {meta.comments}
                </SoftTypography>
              ) : (
                <SoftTypography sx={{ fontFamily: FONT, fontSize: "13.5px", color: T.faint }}>
                  No comments were left.
                </SoftTypography>
              )}
            </SoftBox>
          </SoftBox>

          <SoftBox sx={{ display: "flex", justifyContent: "flex-end", padding: "16px 24px 20px" }}>
            <SoftButton
              variant="outlined"
              color="primary"
              onClick={onClose}
              sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "10px", fontWeight: 600, minWidth: "100px" }}
            >
              Close
            </SoftButton>
          </SoftBox>
        </SoftBox>
      )}
    </Dialog>
  );
};

export default SubmissionDetailDialog;
